<script setup lang="ts">
import { computed } from 'vue';
import type { Props, Emits } from './input';
import { Loadbar } from '@/components/forms/forms';

defineOptions({
	inheritAttrs: false,
});

const props = withDefaults(defineProps<Props>(), {
	size: 's',
	captionType: '',
});

const emit = defineEmits<Emits>();

const localValue = computed({
	get() {
		return props.modelValue;
	},
	set(value) {
		emit('update:modelValue', value);
	},
});

const placeholder = computed(() => {
	if (props.addCleaner && !props.title) {
		return '';
	}
	if (props.captionType !== '') {
		return '';
	}

	return props.title;
});

const clean = (event: Event) => {
	if(props.modelValue){
		event.stopPropagation();
	}

	emit('update:modelValue', '');
};
</script>

<template>
	<label
		:class="{
			['top-input']: true,
			['top-size_' + size]: true,
			['top-disabled']: disabled,
			['top-input-' + modificator]: !!modificator,
			['top-input-withCleaner']: addCleaner && localValue && localValue !== '0000-00-00',
			['top-formsCaptionWrapper']: captionType !== '',
			['top-formsCaptionWrapper-always']: captionType === 'top',
		}"
		:data-top-icon="icon"
		:data-top-icon2="icon2"
	>
		<Loadbar v-if="isLoading"/>

		<input
			type="text"
			:class="{
				['top-input_input']: true,
				['top-input_input-' + modificator]: !!modificator,
				['top-forms-focusable']: !disabled,
				['top-error']: isError,
			}"
			autocomplete="off_always"
			:name="name"
			v-model="localValue"
			:title="title"
			:placeholder="placeholder"
			:disabled="disabled"
			:readonly="readonly"
			@keydown.esc="(event) => (addCleaner || !!$slots.btn) && clean(event)"
			:="$attrs"
		>

		<span
			v-if="addCleaner && localValue && localValue !== '0000-00-00'"
			class="top-input_cleaner"
			data-top-icon=""
			@click.prevent="clean"
		></span>

		<span
			v-if="captionType !== ''"
			class="top-formsCaption"
		>
			{{ title }}
		</span>

		<!-- @slot Слот для вставки произвольного кода в label -->
		<slot></slot>
	</label>
</template>

<style module>
.top-input {
	--top-forms_clear-width: 0px;

	border-radius: var(--top-forms-radius);
	width: 180px;
	position: relative;
	display: inline-flex;
	align-items: center;
	gap: var(--top-gap-1);
}

.top-input_input {
	background: var(--top-forms-background-color);
	height: var(--top-forms-base-height);
	padding: 0;
	padding-right: calc(max(var(--top-forms-padding), calc(var(--top-icon2-width) + var(--top-forms_clear-width))));
	padding-left: calc(max(var(--top-forms-padding), var(--top-icon-width)));
}

.top-input_input:hover {
	background: var(--top-forms-background-color-hover);
}

.top-input_input:focus {
	outline-color: var(--color-theme-75);
	outline-offset: 0;
}

/* значки */
.top-input[data-top-icon]:before,
.top-input[data-top-icon2]:after {
	--top-icon-color: var(--color-text-secondary);
	--top-icon2-color: var(--color-text-secondary);

	position: absolute;
	z-index: 3;
}

.top-input[data-top-icon]:before {
	left: 0;
}

.top-input[data-top-icon2]:after {
	right: 0;
}

/* cleaner */
.top-input-withCleaner {
	--top-forms_clear-width: 24px;
}

.top-input_cleaner {
	--top-icon-size: 16px;
	--top-icon-width: 20px;
	--top-icon-color: var(--color-text-3);

	cursor: pointer;
	border-radius: 50%;
	width: 20px;
	height: 16px;
	position: absolute;
	right: calc(var(--top-forms-border-width) + max(var(--top-icon2-width), var(--top-forms-padding) / 2));
	z-index: 1;
	display: flex;
	align-items: center;
	justify-content: center;
}

.top-input_cleaner:hover {
	--top-icon-color: var(--color-text-2);
}

/* TODO: перенести в inputsRange */
.top-input_input {
	width: 50%;
	flex-grow: 1;
}

/* top-loadbar */
.top-input .top-loadbar{
	border-radius: var(--top-forms-radius) 0 0 0;
}
</style>